import {
  type FilterValue,
  type OrderGridRow,
  type OrderPerspectiveView,
  formatDeliveryWindow,
} from '@/api/hooks/useOrders'
import { OrderFlagChips } from '@/components/Display/OrderFlagChips'
import { DateWindowFilter } from '@/components/Grid/ColumnFilters/DateWindowFilter'
import { formatTimestamp } from '@/utils/dateFormatters'
import { RoutingCell } from '@components/Display/RoutingCell'
import { OrderLinkRenderer } from '@components/Grid/CellRenderers/OrderLinkRenderer'
import { CheckpointsDots } from '@pages/Dashboard/panels/ConnectionLogsPanel/components/CheckpointsDots'
import type {
  ColDef,
  ICellRendererParams,
  SetFilterValuesFuncParams,
  ValueFormatterParams,
} from 'ag-grid-community'
import {
  ActionsRenderer,
  DeliveryWindowRenderer,
  IntegrationErrorRenderer,
  IntegrationStatusRenderer,
  type LogClickHandler,
  ProductsRenderer,
  TripStatusRenderer,
  UpdatedByRenderer,
  issueTitle,
} from './OrdersCellRenderers'

export type GetFilterOptions = (
  colId: string,
  filterModel: Record<string, unknown>,
) => Promise<FilterValue[]>

interface OrdersColumnDefsOptions {
  /** Opens a log in the details drawer -- the status chip's own log, or
   * whichever log a checkpoint resolves to. */
  onLogClick?: LogClickHandler
  onHistoryClick?: (order: OrderPerspectiveView) => void
  onChangeRequestClick?: (changeId: string, order: OrderPerspectiveView) => void
  dateRange?: { start: string; end: string }
}

/**
 * Values for a server-side set filter dropdown, pulled from
 * /crossroads/order/filter_values via the grid context. The other active
 * filters go along so each dropdown narrows with them (faceting), while the
 * column's own filter is excluded server-side so the selection stays widenable.
 *
 * The endpoint returns value/label pairs, and the two have to stay apart: the
 * filter model carries the stored value (`change_requested`) while the dropdown
 * shows the label ("Change Requested"). AG Grid asks for the values and formats
 * them in two separate callbacks, so the labels from the fetch are held here for
 * the formatter to read.
 */
function setFilterParams(
  colId: string,
): Pick<ColDef<OrderGridRow>, 'filter' | 'filterParams'> {
  // Keyed by `value ?? ''` so the backend's own blank label flows through
  // instead of being duplicated on this side.
  const labels = new Map<string, string>()
  return {
    filter: 'agSetColumnFilter',
    filterParams: {
      values: (params: SetFilterValuesFuncParams<OrderGridRow>) => {
        const { getFilterOptions } = params.context as {
          getFilterOptions: GetFilterOptions
        }
        getFilterOptions(colId, params.api.getFilterModel())
          .then((options) => {
            labels.clear()
            for (const option of options) {
              labels.set(option.value ?? '', option.label)
            }
            params.success(options.map((option) => option.value))
          })
          .catch(() => params.success([]))
      },
      valueFormatter: (params: ValueFormatterParams) => {
        const key = params.value == null ? '' : String(params.value)
        return labels.get(key) ?? key
      },
      refreshValuesOnOpen: true,
    },
  }
}

/**
 * Columns the /find endpoint can't sort or filter: the drop-derived arrays
 * (Products, Origins, ...) and the enrichment-only ones (Checkpoints, Issue),
 * which are assembled after the page is paged rather than being fields the
 * query can reach. A live header control on them would silently do nothing.
 */
const DISPLAY_ONLY: Pick<ColDef, 'sortable' | 'filter' | 'enableRowGroup'> = {
  sortable: false,
  filter: false,
  enableRowGroup: false,
}

export function createOrdersColumnDefs(
  options?: OrdersColumnDefsOptions,
): ColDef[] {
  return [
    {
      headerName: 'Order #',
      field: 'internal_order_number',
      cellRenderer: (params: ICellRendererParams<OrderGridRow>) => {
        return (
          <OrderLinkRenderer
            order_key={params.data?.order_key}
            routing={params.data?.routing}
            myNumber={true}
          />
        )
      },
      minWidth: 150,
      sortable: true,
      // Order numbers are what the search box is for -- it matches a typed
      // number against every order-number field on both sides of the routing,
      // which a per-column filter on this one perspective can't do.
      filter: false,
      // One group per row otherwise.
      enableRowGroup: false,
    },
    {
      headerName: 'Partner Order #',
      field: 'partner_order_number',
      cellRenderer: (params: ICellRendererParams<OrderGridRow>) => {
        return (
          <OrderLinkRenderer
            order_key={params.data?.order_key}
            routing={params.data?.routing}
            myNumber={false}
          />
        )
      },
      minWidth: 150,
      sortable: true,
      // Covered by the search box too -- see Order # above.
      filter: false,
      enableRowGroup: false,
    },
    {
      headerName: 'PO',
      field: 'po',
      minWidth: 100,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: { filterOptions: ['equals', 'contains', 'startsWith'] },
      enableRowGroup: false,
    },
    {
      headerName: 'Order Status',
      minWidth: 180,
      field: 'trip_status',
      cellRenderer: (params: ICellRendererParams<OrderGridRow>) => {
        if (params.node.group) return params.value
        return TripStatusRenderer({ value: params.value as string | null })
      },
      sortable: true,
      enableRowGroup: true,
      ...setFilterParams('trip_status'),
    },
    {
      headerName: 'Integration Status',
      field: 'status',
      cellRenderer: (params: ICellRendererParams<OrderGridRow>) => {
        if (params.node.group) return params.value
        return IntegrationStatusRenderer({
          data: params.data,
          onLogClick: options?.onLogClick,
          onChangeRequestClick: options?.onChangeRequestClick,
        })
      },
      minWidth: 160,
      sortable: true,
      enableRowGroup: true,
      ...setFilterParams('status'),
    },
    {
      headerName: 'Checkpoints',
      field: 'checkpoints',
      minWidth: 180,
      ...DISPLAY_ONLY,
      cellRenderer: (params: ICellRendererParams<OrderGridRow>) => {
        const row = params.data
        if (!row?.checkpoints) return null
        return (
          <CheckpointsDots
            checkpoints={row.checkpoints}
            logGroupId={row.log_group_id ?? undefined}
            onLogSelected={(log) =>
              options?.onLogClick?.(log, row.log_group_id ?? null)
            }
          />
        )
      },
    },
    {
      headerName: 'Flags',
      field: 'flags',
      minWidth: 180,
      sortable: false,
      enableRowGroup: false,
      ...setFilterParams('flags'),
      cellRenderer: (params: ICellRendererParams<OrderGridRow>) => (
        <OrderFlagChips flags={params.data?.flags ?? []} />
      ),
    },
    {
      headerName: 'Products',
      field: 'product_ratios',
      cellRenderer: ProductsRenderer,
      minWidth: 180,
      flex: 1,
      ...DISPLAY_ONLY,
      valueGetter: (params) => {
        const products = params.data?.product_ratios
        if (!Array.isArray(products)) return ''
        return products
          .map((p) => {
            const parts = [p.name]
            if (p.amount) parts.push(`${p.amount}%`)
            if (p.volume) parts.push(`${p.volume} gal`)
            return parts.join(' ')
          })
          .join(', ')
      },
      autoHeight: true,
      wrapText: true,
    },
    {
      headerName: 'Origins',
      field: 'origins',
      minWidth: 150,
      ...DISPLAY_ONLY,
      valueGetter: (params) => params.data?.origins?.join(', ') || '',
    },
    {
      headerName: 'Destinations',
      field: 'destinations',
      minWidth: 160,
      ...DISPLAY_ONLY,
      valueGetter: (params) => params.data?.destinations?.join(', ') || '',
    },
    {
      headerName: 'Tank IDs',
      field: 'tank_ids',
      minWidth: 140,
      hide: true,
      ...DISPLAY_ONLY,
      valueGetter: (params) => params.data?.tank_ids?.join(', ') || '',
    },
    {
      headerName: 'Delivery Window',
      field: 'delivery_window_start',
      cellRenderer: DeliveryWindowRenderer,
      minWidth: 320,
      sortable: true,
      enableRowGroup: false,
      // "Deliverable on this day" -- a containment test across the window's two
      // ends, which the backend's custom_filter resolves (app/order/grid.py).
      filter: DateWindowFilter,
      filterParams: {
        startField: 'delivery_window_start',
        endField: 'delivery_window_end',
      },
      valueGetter: (params) =>
        formatDeliveryWindow(
          params.data?.delivery_window_start,
          params.data?.delivery_window_end,
        ) || '',
    },
    {
      headerName: 'ETA',
      field: 'eta',
      minWidth: 160,
      sortable: true,
      filter: 'agDateColumnFilter',
      enableRowGroup: false,
      valueFormatter: (params) => formatTimestamp(params.value),
    },
    {
      headerName: 'Notes',
      field: 'note',
      minWidth: 150,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: { filterOptions: ['contains', 'equals'] },
      enableRowGroup: false,
    },
    {
      headerName: 'Assigned To',
      field: 'assigned_to',
      minWidth: 150,
      hide: true,
      sortable: true,
      enableRowGroup: true,
      ...setFilterParams('assigned_to'),
    },
    {
      headerName: 'Suppliers',
      field: 'suppliers',
      minWidth: 180,
      hide: true,
      ...DISPLAY_ONLY,
      valueGetter: (params) => params.data?.suppliers?.join(', ') || '',
    },
    {
      headerName: 'Price Types',
      field: 'price_types',
      minWidth: 180,
      hide: true,
      ...DISPLAY_ONLY,
      valueGetter: (params) => params.data?.price_types?.join(', ') || '',
    },
    {
      headerName: 'Contract #s',
      field: 'contracts',
      minWidth: 180,
      hide: true,
      ...DISPLAY_ONLY,
      valueGetter: (params) => params.data?.contracts?.join(', ') || '',
    },
    {
      headerName: 'Partner',
      field: 'partner_tenant',
      minWidth: 160,
      sortable: true,
      enableRowGroup: true,
      ...setFilterParams('partner_tenant'),
    },
    {
      headerName: 'Routing',
      field: 'routing',
      minWidth: 220,
      // A whole subdocument -- nothing for a header control to sort or match on.
      ...DISPLAY_ONLY,
      cellRenderer: (params: ICellRendererParams<OrderGridRow>) => (
        <RoutingCell routing={params.data?.routing} />
      ),
    },
    {
      headerName: 'Updated By',
      field: 'updated_date',
      cellRenderer: (params: ICellRendererParams<OrderGridRow>) => {
        if (params.node.group) return params.value
        return UpdatedByRenderer({
          data: params.data,
          onHistoryClick: options?.onHistoryClick,
        })
      },
      minWidth: 180,
      sortable: true,
      enableRowGroup: false,
      // Falls back to created_date, mirrored by updated_or_created on the
      // backend so the sort agrees with what the cell shows.
      valueGetter: (params) =>
        params.data?.updated_date ?? params.data?.created_date,
      filter: 'agDateColumnFilter',
      hide: true,
    },
    {
      colId: 'issue',
      headerName: 'Issue',
      ...DISPLAY_ONLY,
      valueGetter: (params) => issueTitle(params.data?.log),
      cellRenderer: (params: ICellRendererParams<OrderGridRow>) => {
        const row = params.data
        const log = row?.log
        return IntegrationErrorRenderer({
          log,
          onClick: log
            ? () => options?.onLogClick?.(log, row?.log_group_id ?? null)
            : undefined,
        })
      },
      hide: true,
    },
    {
      headerName: '',
      field: 'actions',
      cellRenderer: (params: ICellRendererParams<OrderGridRow>) => {
        if (params.node.group) return null
        return ActionsRenderer({
          data: params.data,
          onHistoryClick: options?.onHistoryClick,
        })
      },
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      ...DISPLAY_ONLY,
      suppressMenu: true,
      pinned: 'right',
    },
  ]
}
